import React, { FC, Fragment } from 'react';
import Grid from '@mui/material/Grid';
import Material from 'components/property-settings/material/material';
import Shade from 'components/property-settings/shade/shade';
import ContactType from 'components/property-settings/contact-type/contact-type';
import OcclusionType from 'components/property-settings/occlusion-type/occlusion-type';
import MetalDesign from 'components/property-settings/metal-design/metal-design';
import PonticDesign from 'components/property-settings/pontic-design/pontic-design';
import Retainment from 'components/property-settings/retainment/retainment';
import ImplantSteps from 'components/property-settings/implant-steps/implant-steps';
import DentureSteps from 'components/property-settings/denture-steps/denture-steps';
import { Box } from '@mui/material';
import { ConstructionPropertyType } from 'models/order-input/shared';
import ClaspTypes from 'components/property-settings/clasp-types/clasp-types';
import NumberOfUnit from 'components/property-settings/number-of-unit/number-of-unit';
import TestingSteps from 'components/property-settings/testing-steps/testing-steps';
import ClaspTeeth from 'components/property-settings/clasp-teeth/clasp-teeth';
import SpringTeeth from 'components/property-settings/spring-teeth/spring-teeth';
import SpringTypes from 'components/property-settings/spring-types/spring-types';
import ClaspMaterial from 'components/property-settings/clasp-material/clasp-material';
import FacialLoop from 'components/property-settings/facial-loop/facial-loop';
import Protrusion from 'components/property-settings/protrusion/protrusion';
import ExpansionScrew from 'components/property-settings/expansion-screw/expansion-screw';
import OrthodonticsColor from 'components/property-settings/orthodontics-color/orthodontics-color';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { useConstructions } from 'context/construction-context';

const ConstructionProperties: FC = () => {
    const {
        construction,
        numberOfUnitsOption,
        onSettingsSelection,
        onShadeChange,
        onMaterialsChange,
        availableMaterials,
        numberOfUnitsValues,
        onNumberOfUnitsChange,
        onTeethSettingsChange,
        hasType,
        isRequired,
    } = useConstructions();

    const currentOrganization = currentOrgVar();
    const getSettingOptions = (settingName: string) => {
        return customerSettings.getCustomerSetting(currentOrganization.customerSettings, settingName);
    };
    const claspMaterialOptions = getSettingOptions(CONSTANTS.SETTINGS_NAMES.CLASP_MATERIAL);
    const orthodonticsColorOptions = getSettingOptions(CONSTANTS.SETTINGS_NAMES.ORTHODONTICS_COLORS);
    const facialLoopOptions = getSettingOptions(CONSTANTS.SETTINGS_NAMES.FACIAL_LOOP);
    const claspTypeOptions = getSettingOptions(CONSTANTS.SETTINGS_NAMES.CLASP_TYPES);
    const springTypeOptions = getSettingOptions(CONSTANTS.SETTINGS_NAMES.SPRING_TYPES);
    const metalDesignOptions = getSettingOptions(CONSTANTS.SETTINGS_NAMES.METAL_DESIGN);
    const ponticDesignOptions = getSettingOptions(CONSTANTS.SETTINGS_NAMES.PONTIC_DESIGN);
    const testingStepsOptions = getSettingOptions(CONSTANTS.SETTINGS_NAMES.TESTING_STEPS);
    const occlusionOptions = getSettingOptions(CONSTANTS.SETTINGS_NAMES.OCCLUSION_TYPE);
    const contactTypeOptions = getSettingOptions(CONSTANTS.SETTINGS_NAMES.CONTACT_TYPE);
    const implantStepsOptions = getSettingOptions(CONSTANTS.SETTINGS_NAMES.IMPLANT_STEPS);
    const dentureStepsOptions = getSettingOptions(CONSTANTS.SETTINGS_NAMES.DENTURE_STEPS);

    const onSettingSelection = onSettingsSelection;

    const handleShadeChange = onShadeChange;

    const handleMaterialsChange = onMaterialsChange;

    const handleNumberOfUnitsChange = onNumberOfUnitsChange;

    const handleTeethSettingsChange = onTeethSettingsChange;

    return (
        <Fragment>
            <Box flexGrow={1}>
                <Grid container spacing={2}>
                    {hasType(ConstructionPropertyType.MATERIAL) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <Material
                                isRequired={isRequired(ConstructionPropertyType.MATERIAL)}
                                material={construction.settings?.material}
                                otherMaterialValue={construction.settings?.otherMaterial}
                                onChange={handleMaterialsChange}
                                options={availableMaterials}
                            />
                        </Grid>
                    ) : undefined}
                    {hasType(ConstructionPropertyType.SHADE) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <Shade
                                isRequired={isRequired(ConstructionPropertyType.SHADE)}
                                shade={construction.settings?.shade}
                                color={construction.settings?.color}
                                colorSampling={construction.settings?.colorSampling}
                                onChange={handleShadeChange}
                            />
                        </Grid>
                    ) : undefined}
                    {numberOfUnitsOption.length > 0 ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <NumberOfUnit
                                numberOfUnitsValues={numberOfUnitsValues}
                                options={numberOfUnitsOption}
                                onChange={handleNumberOfUnitsChange}
                            />
                        </Grid>
                    ) : undefined}
                    {hasType(ConstructionPropertyType.METAL_DESIGN) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <MetalDesign
                                isRequired={isRequired(ConstructionPropertyType.METAL_DESIGN)}
                                design={construction.settings?.design}
                                handleSettings={onSettingSelection}
                                options={metalDesignOptions}
                            />
                        </Grid>
                    ) : undefined}
                    {hasType(ConstructionPropertyType.PONTIC_DESIGN) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <PonticDesign
                                isRequired={isRequired(ConstructionPropertyType.PONTIC_DESIGN)}
                                ponticDesign={construction.settings?.ponticDesign}
                                handleSettings={onSettingSelection}
                                options={ponticDesignOptions}
                            />
                        </Grid>
                    ) : undefined}
                    {hasType(ConstructionPropertyType.RETAINMENT) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <Retainment
                                isRequired={isRequired(ConstructionPropertyType.RETAINMENT)}
                                retainment={construction.settings?.retainment}
                                handleSettings={onSettingSelection}
                            />
                        </Grid>
                    ) : undefined}
                    {hasType(ConstructionPropertyType.CLASP_TEETH) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <ClaspTeeth
                                isRequired={isRequired(ConstructionPropertyType.CLASP_TEETH)}
                                claspTeeth={construction.settings?.claspTeeth}
                                handleTeethSettings={handleTeethSettingsChange}
                            />
                        </Grid>
                    ) : undefined}
                    {hasType(ConstructionPropertyType.CLASP_MATERIAL) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <ClaspMaterial
                                isRequired={isRequired(ConstructionPropertyType.CLASP_MATERIAL)}
                                claspMaterial={construction.settings?.claspMaterial}
                                handleSettings={onSettingSelection}
                                options={claspMaterialOptions}
                            />
                        </Grid>
                    ) : undefined}
                    {hasType(ConstructionPropertyType.ORTHODONTICS_COLORS) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <OrthodonticsColor
                                isRequired={isRequired(ConstructionPropertyType.ORTHODONTICS_COLORS)}
                                partOrthoColor={construction.settings?.orthColor}
                                handleSettings={onSettingSelection}
                                options={orthodonticsColorOptions}
                            />
                        </Grid>
                    ) : undefined}
                    {hasType(ConstructionPropertyType.FACIAL_LOOP) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <FacialLoop
                                isRequired={isRequired(ConstructionPropertyType.FACIAL_LOOP)}
                                facialLoop={construction.settings?.facialLoop}
                                handleSettings={onSettingSelection}
                                options={facialLoopOptions}
                            />
                        </Grid>
                    ) : undefined}
                    {hasType(ConstructionPropertyType.CLASP_TYPES) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <ClaspTypes
                                isRequired={isRequired(ConstructionPropertyType.CLASP_TYPES)}
                                claspType={construction.settings?.claspType}
                                handleSettings={onSettingSelection}
                                options={claspTypeOptions}
                            />
                        </Grid>
                    ) : undefined}
                    {hasType(ConstructionPropertyType.SPRING_TYPES) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <SpringTypes
                                isRequired={isRequired(ConstructionPropertyType.SPRING_TYPES)}
                                springType={construction.settings?.springType}
                                handleSettings={onSettingSelection}
                                options={springTypeOptions}
                            />
                        </Grid>
                    ) : undefined}
                    {hasType(ConstructionPropertyType.SPRING_TEETH) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <SpringTeeth
                                isRequired={isRequired(ConstructionPropertyType.SPRING_TEETH)}
                                springTeeth={construction.settings?.springTeeth}
                                handleTeethSettings={handleTeethSettingsChange}
                            />
                        </Grid>
                    ) : undefined}
                    {hasType(ConstructionPropertyType.PROTRUSION) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <Protrusion
                                isRequired={isRequired(ConstructionPropertyType.PROTRUSION)}
                                partProtrusion={construction.settings?.protrusion}
                                handleSettings={onSettingSelection}
                            />
                        </Grid>
                    ) : undefined}
                    {hasType(ConstructionPropertyType.EXPANSION_SCREW) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <ExpansionScrew
                                isRequired={isRequired(ConstructionPropertyType.EXPANSION_SCREW)}
                                partExpansionScrew={construction.settings?.expansionScrew}
                                handleSettings={onSettingSelection}
                            />
                        </Grid>
                    ) : undefined}
                    {hasType(ConstructionPropertyType.CONTACT_TYPE) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <ContactType
                                isRequired={isRequired(ConstructionPropertyType.CONTACT_TYPE)}
                                contactType={construction.settings?.contactType}
                                handleSettings={onSettingSelection}
                                options={contactTypeOptions}
                            />
                        </Grid>
                    ) : undefined}
                    {hasType(ConstructionPropertyType.OCCLUSION_TYPE) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <OcclusionType
                                isRequired={isRequired(ConstructionPropertyType.OCCLUSION_TYPE)}
                                occlusion={construction.settings?.occlusion}
                                handleSettings={onSettingSelection}
                                options={occlusionOptions}
                            />
                        </Grid>
                    ) : undefined}
                    {hasType(ConstructionPropertyType.IMPLANT_STEPS) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <ImplantSteps
                                isRequired={isRequired(ConstructionPropertyType.IMPLANT_STEPS)}
                                implantSteps={construction.settings?.implantSteps}
                                handleSettings={onSettingSelection}
                                options={implantStepsOptions}
                            />
                        </Grid>
                    ) : undefined}
                    {hasType(ConstructionPropertyType.DENTURE_STEPS) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <DentureSteps
                                isRequired={isRequired(ConstructionPropertyType.DENTURE_STEPS)}
                                dentureSteps={construction.settings?.dentureSteps}
                                handleSettings={onSettingSelection}
                                options={dentureStepsOptions}
                            />
                        </Grid>
                    ) : undefined}
                    {hasType(ConstructionPropertyType.TESTING_STEPS) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <TestingSteps
                                isRequired={isRequired(ConstructionPropertyType.TESTING_STEPS)}
                                testingSteps={construction.settings?.testingSteps}
                                handleSettings={onSettingSelection}
                                options={testingStepsOptions}
                            />
                        </Grid>
                    ) : undefined}
                </Grid>
            </Box>
        </Fragment>
    );
};

export default ConstructionProperties;
